/**
 * Input Sanitization Utilities
 * Provides functions to sanitize user input and prevent XSS/injection attacks
 */

/**
 * Sanitize general text input
 * Removes dangerous characters and limits length
 * @param {string} value - The input value to sanitize
 * @param {number} maxLength - Maximum allowed length (default: 100)
 * @returns {string} - Sanitized value
 */
export const sanitizeText = (value, maxLength = 100) => {
    if (typeof value !== 'string') return '';

    return value
        // .trim() // REMOVED to allow typing spaces
        .replace(/[<>"'`]/g, '') // Remove potentially dangerous characters
        .slice(0, maxLength);
};

/**
 * Sanitize reps input (can be text like "8-12", "AMRAP", or numbers)
 * Allows alphanumeric, hyphens, spaces but removes script characters
 * @param {string} value - The reps value to sanitize
 * @returns {string} - Sanitized reps value
 */
export const sanitizeReps = (value) => {
    if (typeof value !== 'string' && typeof value !== 'number') return '';

    const strValue = String(value);

    return strValue
        .replace(/[<>"'`\\/()\[\]{}]/g, '') // Remove dangerous and script characters
        .replace(/script/gi, '') // Remove "script" word
        .slice(0, 20); // Reasonable max length for reps notation
};

/**
 * Sanitize tempo notation (like "3-0-1-0")
 * Only allows numbers, hyphens, and common tempo notations
 * @param {string} value - The tempo value to sanitize
 * @returns {string} - Sanitized tempo value
 */
export const sanitizeTempo = (value) => {
    if (typeof value !== 'string') return '';

    return value
        .replace(/[^0-9\-X]/gi, '') // Only allow numbers, hyphens, and X (for explosive)
        .slice(0, 15);
};

/**
 * Sanitize username input
 * Only allows alphanumeric characters, underscores, and hyphens
 * @param {string} value - The username to sanitize
 * @returns {string} - Sanitized username
 */
export const sanitizeUsername = (value) => {
    if (typeof value !== 'string') return '';

    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '') // Only allow alphanumeric, underscore, hyphen
        .slice(0, 50);
};

/**
 * Sanitize number input
 * Ensures value is a valid number within range
 * @param {any} value - The value to sanitize
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {number} - Sanitized number or 0 if invalid
 */
export const sanitizeNumber = (value, min = 0, max = 999999) => {
    const num = parseFloat(value);

    if (isNaN(num)) return 0;
    if (num < min) return min;
    if (num > max) return max;

    return num;
};

/**
 * Sanitize email input
 * Basic email format validation
 * @param {string} value - The email to sanitize
 * @returns {string} - Sanitized email or empty string
 */
export const sanitizeEmail = (value) => {
    if (typeof value !== 'string') return '';

    const email = value.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    return emailRegex.test(email) ? email : '';
};

/**
 * Sanitize URL input
 * Ensures URL is valid and uses safe protocol
 * @param {string} value - The URL to sanitize
 * @returns {string} - Sanitized URL or empty string
 */
export const sanitizeUrl = (value) => {
    if (typeof value !== 'string') return '';

    try {
        const url = new URL(value.trim());

        // Only allow http, https protocols
        if (!['http:', 'https:'].includes(url.protocol)) {
            return '';
        }

        return url.href;
    } catch {
        return '';
    }
};

/**
 * Sanitize HTML content (for rich text editors)
 * Removes script tags and dangerous attributes
 * @param {string} html - The HTML to sanitize
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHtml = (html) => {
    if (typeof html !== 'string') return '';

    return html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
        .replace(/on\w+="[^"]*"/g, '') // Remove inline event handlers
        .replace(/javascript:/gi, ''); // Remove javascript: protocol
};

/**
 * Validate and sanitize object keys
 * Ensures only allowed keys are present
 * @param {object} obj - The object to validate
 * @param {Array<string>} allowedKeys - Array of allowed keys
 * @returns {object} - Object with only allowed keys
 */
export const sanitizeObject = (obj, allowedKeys) => {
    if (typeof obj !== 'object' || obj === null) return {};

    const sanitized = {};

    for (const key of allowedKeys) {
        if (key in obj) {
            sanitized[key] = obj[key];
        }
    }

    return sanitized;
};

/**
 * Rate limit tracker
 * Prevents abuse by tracking action timestamps
 */
class RateLimiter {
    constructor() {
        this.actions = new Map();
    }

    /**
     * Check if action is allowed
     * @param {string} action - Action identifier (e.g., 'friend_request')
     * @param {number} limitMs - Minimum time between actions in milliseconds
     * @returns {boolean} - True if action is allowed
     */
    canPerformAction(action, limitMs = 1000) {
        const now = Date.now();
        const lastAction = this.actions.get(action);

        if (!lastAction || now - lastAction >= limitMs) {
            this.actions.set(action, now);
            return true;
        }

        return false;
    }

    /**
     * Clear all rate limit history
     */
    clear() {
        this.actions.clear();
    }
}

export const rateLimiter = new RateLimiter();

/**
 * Sanitize object for Firestore
 * Recursively removes undefined values (converts to null or omits)
 * @param {any} obj - The object to sanitize
 * @returns {any} - Sanitized object
 */
export const sanitizeForFirestore = (obj) => {
    if (obj === undefined) return undefined;
    if (obj === null) return null;
    if (typeof obj !== 'object') return obj;
    if (obj instanceof Date) return obj;
    if (Array.isArray(obj)) return obj.map(item => {
        const val = sanitizeForFirestore(item);
        return val === undefined ? null : val; // Arrays should preserve length with nulls or filter? Firestore arrays can't have undefined. Let's map undefined to null for arrays.
    });

    const newObj = {};
    for (const key in obj) {
        const val = sanitizeForFirestore(obj[key]);
        if (val !== undefined) {
            newObj[key] = val;
        }
    }
    return newObj;
};
